import Vue from 'vue'
import { AnalyticsEvent, AnalyticsTarget, SendInBlueEvent } from "./AnalyticsEventTypes";
import { getGAEventParametersFromAnalyticsEvent, prepareEventParameters } from "./AnalyticsEventUtils";
import { BackEndService } from "../../../utils/BackEndService";
import SimplifiedAnalytics from "./SimpliedAnalytics";
import { userService } from "../UserService";
import { AnalyticsCondition, COMPARATORS } from "../models/AnalyticsCondition";
import * as amplitude from '@amplitude/analytics-browser';
import { isInMobileApp } from '../../../utils/MobileUtils';

class AnalyticsController {

  readonly isAmplitudeEnabled = process.env.AMPLITUDE_API_KEY;
  readonly isGAEnabled = process.env.ENABLE_GA;
  readonly isSendInBlueEnabled = process.env.SENDINBLUE_API_KEY;
  readonly isFirebaseCrashlyticsEnabled = isInMobileApp()

  public simplifiedAnalytics: SimplifiedAnalytics[] = []

  private tokatabEventListeners: { eventName: string, callback: Function }[] = [];

  constructor() {
    if (this.isAmplitudeEnabled) {
      amplitude.init(this.isAmplitudeEnabled, {
        minIdLength: 1,
        autocapture: false
      })
    }
  }

  /* Config */

  public setAnalyticsProperties(userData: any) {
    this.setupGAIfEnabled(userData.id);
    this.setupAmplitudeIfEnabled(userData);
    this.setupSendInBlueIfEnabled(userData);
    this.setupFirebaseCrashlyticsIfEnabled(userData)
  }

  /* Logging */

  /**
   * Just a method shortcut to make code smaller
   */
  public logBasicAnalyticsEvent(title: string, category: string, additionalParameters: any = undefined, gaLabelIdentifier = '', saveInBackend = false) {
    this.logAnalyticsEvent({ title, category, gaLabelIdentifier, additionalParameters }, 'All', saveInBackend)
  }

  // TODO: saveInBackEnd should be merge with targets
  public logAnalyticsEvent(event: AnalyticsEvent, target: AnalyticsTarget = 'All', saveInBackEnd = false) {
    event.parameters = prepareEventParameters(event);
    switch (target) {
      case "All":
        this.logAmplitudeEvent(event);
        this.logGAEvent(event);
        this.logFirebaseCrashlyticsEvent(event)
        break;
      case "allWithSlack":
        this.logAmplitudeEvent(event);
        this.logGAEvent(event);
        this.logFirebaseCrashlyticsEvent(event)
        this.logSlackEvent(event);
        break;
      case "Amplitude":
        this.logAmplitudeEvent(event);
        break;
      case "GA":
        this.logGAEvent(event);
        break;
      case "Slack":
        this.logSlackEvent(event);
        break;
      case "NonGA":
        this.logAmplitudeEvent(event);
        break;
    }
    if (saveInBackEnd) {
      this.logTokatabEvent(event)
    }
  }

  /* GA & Amplitude Interface */

  private logAmplitudeEvent(event: AnalyticsEvent) {
    try {
      if (this.isAmplitudeEnabled) {
        amplitude.logEvent(event.title, event.parameters);
      }
      else {
        console.log('Log Event Amplitude', event.title, event.parameters);
      }
    }
    catch (e) {
      console.error('issue with amplitude event')
    }
  }

  private logGAEvent(event: AnalyticsEvent) {
    try {
      const gaEventParameters = getGAEventParametersFromAnalyticsEvent(event);
      if (this.isGAEnabled) {
        Vue.prototype.$gtag.event(event.title, gaEventParameters);
      }
      else {
        console.log('Log Event GA', event.title, gaEventParameters);
      }
    }
    catch (e) {
      console.error('issue with GA event')
    }
  }

  private async logSlackEvent(event: AnalyticsEvent) {
    await BackEndService.sendPostRequest('analytics/slack', event)
  }


  /**
   * Used for personalized mailing
   * @param event
   */
  public logSendInBlueEvent(event: SendInBlueEvent) {
    try {
      if (this.isSendInBlueEnabled) {
        // @ts-ignore // False negative
        window.sendinblue.track(event.event_name, {
          email: userService.user.email,
          FIRSTNAME: userService.user.firstname,
          LASTNAME: userService.user.lastname,
        }, event.event_data);
      }
      else {
        console.log('Log Event Sendinblue', event.event_name, event.event_data);
      }
    }
    catch (e) {
      console.error('issue with Sendinblue event')
    }
  }

  /**
   * Simplified logging system used for dynamic tips
   * @param event
   * @private
   */
  private logTokatabEvent(event: AnalyticsEvent) {
    try {
      BackEndService.sendPostRequest('analytics', { "action": event.title }).then();

      const simplifiedAnalyticsIndexIfExists = this.simplifiedAnalytics.findIndex(analytics => analytics.action === event.title)
      if (simplifiedAnalyticsIndexIfExists !== -1) {
        this.simplifiedAnalytics[simplifiedAnalyticsIndexIfExists].occurrence++;
      }
      else {
        this.simplifiedAnalytics.push({ action: event.title, occurrence: 1 })
      }
      this.runAllListenersToTokatabEvent(event.title);
    }
    catch (e) {
      console.error('issue with Tokatab event')
    }
  }

  /**
   * Log to add context when app crash in crashlytics
   * @param event
   * @private
   */
  private logFirebaseCrashlyticsEvent(event: AnalyticsEvent) {
    try {
      if (this.isFirebaseCrashlyticsEnabled) {
        if (Vue.prototype.MobileMonitoring.isEnabled) {
          Vue.prototype.MobileMonitoring.log(`[${event.category}] : ${event.title}`)
        }
      }
    }
    catch (e) {
      console.error('issue with FirebaseCrashlytics event')
    }
  }

  /* Setup Methods for analytics */

  private setupGAIfEnabled(userId: number) {
    try {
      if (this.isGAEnabled) {
        Vue.prototype.$gtag.set('user_properties', { user_id: userId });
      }
    }
    catch (e) {
      console.error('issue with GA setup')
    }
  }

  private setupAmplitudeIfEnabled(userData: any) {
    try {
      if (this.isAmplitudeEnabled) {
        amplitude.setUserId(userData.id);
        const identify = new amplitude.Identify()
          .set('firstname', userData.firstname)
          .set('lastname', userData.lastname)
          .set('email', userData.email)
          .set('createdFrom', userData.created_from)
          .set('isPremium', userData.premium)
          .set('plan', userData.current_plan)
          .set('status', userData.status)
          .set('subscribedAt', userData.subscribed_at || 'null')
          .set('unsubscribedAt', userData.unsubscribed_at || 'null')
          .set('paymentGroupTesting', userData.payment_group_testing || 'null')

        amplitude.identify(identify);
      }
    }
    catch (e) {
      console.error('issue with Amplitude setup')
    }
  }

  public addAmplitudeIdentityInformation(settingName: string, settingValue: any) {
    try {
      if (this.isAmplitudeEnabled) {
        const identify = new amplitude.Identify().set(settingName, settingValue);
        amplitude.identify(identify);
      }
    }
    catch (e) {
      console.error('issue with Amplitude setup')
    }
  }

  private setupSendInBlueIfEnabled(user: any) {
    try {
      if (this.isSendInBlueEnabled) {
        // @ts-ignore
        window.sendinblue.identify(user.email, {
          PRENOM: user.firstname,
          NOM: user.lastname,
          LAST_USAGE: new Date().toLocaleDateString('fr-FR'),
        });
      }
    }
    catch (e) {
      console.error('issue with Sendinblue setup')
    }
  }

  private setupFirebaseCrashlyticsIfEnabled(user: any) {
    try {
      if (this.isFirebaseCrashlyticsEnabled) {
        if (Vue.prototype.MobileMonitoring.isEnabled) {
          Vue.prototype.MobileMonitoring.setUserId(user.id.toString())
        }
      }
    }
    catch (e) {
      console.error('issue with FirebaseCrashlytics setup')
    }
  }

  /* Event Listener to trigger any content */

  public addListenerToTokatabEvent(triggerEventName: string, callback: Function) {
    this.tokatabEventListeners.push({ eventName: triggerEventName, callback })
  }
  private runAllListenersToTokatabEvent(matchEventName: string) {
    this.tokatabEventListeners.filter(({ eventName }) => eventName === matchEventName)
      .every(({ eventName, callback }) => { // Use every to hack foreach and allow to quit foreach on first success
        if (callback()) {
          this.tokatabEventListeners = this.tokatabEventListeners.filter(
            (listener) => eventName === listener.eventName && callback === listener.callback)
          return false;
        }
        return true;
      });
  }

  /* Analytics Comparator */

  public areConditionsValid(conditions: AnalyticsCondition[]) {
    return conditions.length === 0 || conditions.every((condition) => this.isConditionValid(condition))
  }
  public isConditionValid(condition: AnalyticsCondition) {
    const analytics: SimplifiedAnalytics = this.simplifiedAnalytics.find((an: SimplifiedAnalytics) => an.action === condition.name) || { occurrence: 0, action: condition.name };
    return !(!analytics || !COMPARATORS[condition.compare](analytics.occurrence, condition.amount));
  }
}

export const analyticsController = new AnalyticsController();
